import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams, } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { formatDate } from "../../../utils/FormatUtils";
import { getLocalGridState, setLocalGridState, } from "../../../utils/GridUtils";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import ColouredChip from "../../../component/ColouredChip";
import { STATE_ACTIVE_FILTERS, STATE_TABLE_UPDATING } from "../pages/atoms/CollectionDetailAtoms";
import { useSddRepository } from "../../../api/repositories/SddRepository";
import { STATUSES } from "../constants/CollectionDetailConstants";
import useToggle from "../hooks/useToggle";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Grid, Typography } from "@mui/material";
import { ArrowBackRounded } from "@mui/icons-material";
import { useAgGridLocale } from "../../../../../config/globalConfig";

const CollectionTable = () => {
    const UNIQUE_ID = "fnn-sdd-mandates";

    const { t } = useTranslation("finance", { keyPrefix: "sdd.collections.details" });

    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const reportType = queryParams.get('reportType');
    const reportDate = queryParams.get('reportDate');

    const { getCollectionReports } = useSddRepository();

    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);
    const [currentRow, setCurrentRow] = useState<any>();

    const [showDownload, toggleDownload] = useToggle();
    const agGridLocale = useAgGridLocale();
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);

    const gridRef = useRef<AgGridReact>(null);

    const [columnDefs] = useState<ColDef[]>([
        {
            field: "id",
            headerName: t("table.receipt-id"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "mandate.debtorName",
            headerName: t("table.receipt-debtorname"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "amount",
            headerName: t("table.receipt-amount"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "reference",
            headerName: t("table.receipt-reference"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "mandate.subscriptionDate",
            headerName: t("table.receipt-subscriptionDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value)
        },
        {
            field: "mandate.id",
            headerName: t("table.receipt-idmandate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "mandate.debtorIban",
            headerName: t("table.receipt-iban"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "status",
            headerName: t("table.receipt-status"),
            minWidth: 100,
            unSortIcon: true,
            sortable: true,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                let color = STATUSES.find(s => s.value === status)?.color?.split(".")[0];
                let tooltipText: string = params.data.errorReason;
                if (!color || color === "neutral") color = "default";
                return {
                    component: () => (
                        <ColouredChip name={status} color={color} tooltip={tooltipText} />
                    ),
                };
            },
        }
    ]);



    function isBeforeToday(date: string) {
        const today: Date = new Date();
        const comparisonDate: Date = new Date(date);
        return today > comparisonDate;
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
            headerCheckboxSelection: true
        },
        noRowsOverlayComponentParams: {
            title: t("table.no-rows.title"),
            content: t("table.no-rows.content")
        },
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(
            createDatasource(filterDefs, gridRef.current.api)
        );
        setSummaryUpdating(true);
    }, [filterDefs]);

    useEffect(() => {
    }, [currentRow]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(
                createDatasource(filterDefs, gridRef.current.api)
            );
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating]);

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].sort.toUpperCase();
                getCollectionReports(id, startRow, endRow - startRow, sort, order, filterDefs)
                    .then((response) => {
                        let result = response.result;

                        if (result["total-amount"] === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result["total-amount"],
                        });
                    })
                    .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        /*         renderInfo: () => (
                    //<NumericSummary query={() => getSummary()} fields={[ID_ACTIVE, ID_INACTIVE]} i18nPath={"collection.counters"}/>
                ), */
        /*         renderActions: () => (
                    <Button fullWidth color={"accent"} variant="contained" component="label"
                        startIcon={<DownloadRounded />}
                        disabled={isSelectedRow}
                        onClick={() => {
                            toggleDownload()
                        }}>
                        {t('buttons.download-report')} 
                    </Button>
                ), */
    };



    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState(),
        });
    }

    return (
        <>

            <Grid item xs={12}>
                <Box color={'primary.light'} style={{ display: 'flex', alignItems: 'center', gap: '1ch' }}>
                    <ArrowBackRounded onClick={() => {
                        navigate("/finance/payments/sdd/collections");
                    }} style={{ cursor: 'pointer' }} />
                    <Typography variant={'body2'} color={'primary.light'} fontWeight={700} textTransform={'uppercase'}>
                    {(reportType) ? "Report " + reportType + " - " + reportDate : ''}
                    </Typography>
                </Box>
            </Grid>

            <AgGridCustomTable
                toolbar={toolbar}
                rowModelType="serverSide"
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows}
                serverSideInfiniteScroll
                gridRef={gridRef}
                columnDefs={columnDefs}
                gridOptions={gridOptions}
                rowSelection={"multiple"}
                suppressRowClickSelection
                noRowsOverlayComponent={noRowsOverlayComponent}
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(
                        createDatasource(filterDefs, gridRef.current!.api)
                    );

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true,
                        });
                    }
                }}
                onColumnMoved={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    if (gridRef.current!.api) {
                        setLocalGridState(UNIQUE_ID, {
                            rows: gridRef.current!.api?.paginationGetPageSize(),
                            cols: getLocalGridState(UNIQUE_ID).cols,
                        });
                    }
                }}
            ></AgGridCustomTable>


        </>
    );
};

export default CollectionTable;
